import { SagaIterator } from 'redux-saga';
import axiosWithoutCredentials from 'axios';
import ImageCompressor from '@xkeshi/image-compressor';
import { cloneDeep, get, set } from 'lodash';
import { actionChannel, call, put, select, take } from 'redux-saga/effects';
import Api from '../../utils/API';
import {
  ICollectionTemplateChangeAction,
  ICollectionUpdateAction,
  IDomainUpdateAction,
  IMainCustomersUpdateAction,
  ISagaAction,
  ISearchCollectionsAction,
  IUploadAppIconAction,
  IUploadCollectionHeaderAction
} from '../../types';
import { IEndCustomerProps } from '../../types/endCustomer';
import {
  changeCollectionState,
  changeCollectionStateSuccess,
  changeCollectionTemplate,
  clearCollection,
  clearGalleries,
  clearImages,
  clearSelected,
  collectionSaveFinished,
  collectionSaveStarted,
  errorsGlobalError,
  fetchCollectionFailed,
  fetchCollectionSuccess,
  fetchImagesSuccess,
  searchCollectionsSuccessAction,
  setIsCropped,
  showNotifyFail,
  showNotifySuccess,
  updateCollectionByPath
} from '../../actions';
import {
  getCollectionHeaderImage,
  getCollectionId,
  getCollectionsNew,
  getImageByID,
  getImagesList
} from '../../selectors';

import { multipleAttempts } from '../global';
import ApiErrors from '../../utils/API/APIErrors';
import { getTranslationKey } from '../../utils';
import { IEndCustomer, IImage } from '../../reducers';

export const initialCollectionFetchSaga = function* (action: ISagaAction<string>): SagaIterator {
  yield call(clearCollectionSaga);
  yield call(fetchCollectionSaga, action);
};

export const clearCollectionSaga = function* (): SagaIterator {
  yield put(clearCollection());
  yield put(clearGalleries());
  yield put(clearImages());
  yield put(clearSelected());
};

export const fetchCollectionSaga = function* ({ payload }: ISagaAction<string>): SagaIterator {
  try {
    const response = yield call(Api.Collections.get, {
      id: payload,
      ownerRequest: true
    });

    if (response.code === 401 || response.code === 403) {
      window.location.href = '/#/collections';
    }

    ApiErrors.checkOnApiError(response);
    yield put(fetchCollectionSuccess(response));
  } catch (e) {
    yield put(fetchCollectionFailed());
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const searchCollectionsSaga = function* ({
  payload
}: ISagaAction<ISearchCollectionsAction>): SagaIterator {
  try {
    const response = yield call(Api.Collections.search, payload);
    ApiErrors.checkOnApiError(response);
    yield put(searchCollectionsSuccessAction(response.result));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const deleteCollectionCustomDomainSaga = function* (): SagaIterator {
  try {
    const collectionID = yield select(getCollectionId);
    yield call(Api.Collections.deleteDomain, {
      site_id: collectionID
    });

    const response = yield put(
      changeCollectionState({
        path: 'customDomain',
        value: {
          domain: '',
          path: '',
          url: ''
        }
      })
    );
    ApiErrors.checkOnApiError(response);
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const updateCollectionByPathSaga = function* ({
  payload
}: ISagaAction<ICollectionUpdateAction>): SagaIterator {
  yield put(collectionSaveStarted());

  try {
    const collectionID = yield select(getCollectionId);
    const response = yield call(Api.Collections.update, {
      collection_id: collectionID,
      path: payload.path,
      value: payload.value
    });
    ApiErrors.checkOnApiError(response);

    const { path, payload: resultPayload } = response;

    if (payload.path === 'customDomain.path' && response.isPathAlreadyTaken) {
      // Force the component to revert localPath by changing reduxPath twice:
      // first to the attempted value, then back to the correct value
      const collection = yield select(getCollectionsNew);
      const oldPath = collection.customDomain.path;
      const attempted = set(cloneDeep(collection), 'customDomain.path', payload.value);
      yield put(changeCollectionStateSuccess(attempted));
      const reverted = set(cloneDeep(collection), 'customDomain.path', oldPath);
      yield put(changeCollectionStateSuccess(reverted));
      yield put(
        showNotifyFail({ message: getTranslationKey('customDomains.path-is-already-in-use') })
      );
    } else {
      yield put(changeCollectionState({ path, value: resultPayload }));
      if (!payload.isSilent && response?.message !== 'Network Error') {
        yield put(showNotifySuccess({}));
      }
    }

    if (response?.message === 'Network Error') {
      yield put(showNotifyFail({ message: getTranslationKey('offlineMessage') }));
    }

    if (payload.path === '_productPricingList') {
      yield call(syncShowOnlyPurchasedPrintsSaga);
    }
  } catch (e) {
    if (payload.path === 'password') {
      yield put(showNotifyFail({ message: getTranslationKey('generalError') }));
    } else {
      // @ts-ignore
      yield put(errorsGlobalError(e));
    }
  } finally {
    yield put(collectionSaveFinished());
  }
};

/**
 * Saves a template switch one part after another. The backend checks a newly picked palette
 * against the template already stored, so the design only goes out once the template is in.
 * Counted as one save throughout, so the preview does not reload between the parts.
 */
export const changeCollectionTemplateSaga = function* ({
  payload: { templateName, type, design }
}: ISagaAction<ICollectionTemplateChangeAction>): SagaIterator {
  yield put(collectionSaveStarted());

  try {
    yield call(
      updateCollectionByPathSaga,
      updateCollectionByPath({ path: 'templateName', value: templateName })
    );
    yield call(
      updateCollectionByPathSaga,
      updateCollectionByPath({ path: 'type', value: type, isSilent: true })
    );

    if (design) {
      yield call(
        updateCollectionByPathSaga,
        updateCollectionByPath({ path: 'design', value: design, isSilent: true })
      );
    }
  } finally {
    yield put(collectionSaveFinished());
  }
};

/** Template switches queue up, so a quick second one cannot overtake the first one's saves. */
export const watchCollectionTemplateChanges = function* (): SagaIterator {
  const channel = yield actionChannel(changeCollectionTemplate);

  while (true) {
    const action = yield take(channel);
    yield call(changeCollectionTemplateSaga, action);
  }
};

export const syncShowOnlyPurchasedPrintsSaga = function* (): SagaIterator {
  try {
    const collectionID = yield select(getCollectionId);
    const collection = yield call(Api.Collections.get, {
      id: collectionID,
      ownerRequest: true
    });
    yield put(
      changeCollectionStateSuccess({
        showOnlyPurchasedPrints: collection.showOnlyPurchasedPrints
      })
    );
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const updateCollectionDomainSaga = function* ({
  payload
}: ISagaAction<IDomainUpdateAction>): SagaIterator {
  try {
    const collectionID = yield select(getCollectionId);
    const { customDomain } = yield select(getCollectionsNew);

    const resultDomain = yield call(Api.Collections.update, {
      collection_id: collectionID,
      path: 'customDomain.domain',
      value: payload.domain
    });
    ApiErrors.checkOnApiError(resultDomain);

    let resultPath = resultDomain;

    if (customDomain.path && payload.path !== customDomain.path) {
      resultPath = yield call(Api.Collections.update, {
        collection_id: collectionID,
        path: 'customDomain.path',
        value: payload.path
      });
      ApiErrors.checkOnApiError(resultPath);
    }

    yield put(
      changeCollectionState({
        path: 'customDomain',
        value: {
          ...customDomain,
          url: get(resultPath, 'payload.url', get(resultDomain, 'payload.url', customDomain.url)),
          domain: get(resultDomain, 'payload.domain', customDomain.domain),
          path: get(resultPath, 'payload.path', customDomain.path)
        }
      })
    );
    if (resultPath.isPathAlreadyTaken) {
      yield put(
        showNotifyFail({ message: getTranslationKey('customDomains.path-is-already-in-use') })
      );
    } else {
      yield put(showNotifySuccess({}));
    }
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const updateMainCustomersSaga = function* ({
  payload
}: ISagaAction<IMainCustomersUpdateAction>): SagaIterator {
  try {
    const collection = yield select(getCollectionsNew);
    const collectionID = yield select(getCollectionId);

    const response = yield call(Api.Collections.update, {
      collection_id: collectionID,
      path: 'mainCustomers',
      value: payload.value
    });
    ApiErrors.checkOnApiError(response);

    yield put(
      changeCollectionStateSuccess(set(cloneDeep(collection), 'mainCustomers', response.value))
    );
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const deleteMainCustomerSaga = function* ({ payload }: ISagaAction<string>): SagaIterator {
  try {
    const collection = yield select(getCollectionsNew);
    const collectionID = yield select(getCollectionId);
    const response = yield call(Api.Collections.deleteMainCustomer, {
      collection_id: collectionID,
      value: payload
    });
    ApiErrors.checkOnApiError(response);
    const { mainCustomers } = response.result.props;

    const updatedMainCustomers = collection.mainCustomers.filter((customer: IEndCustomer) =>
      mainCustomers.includes(customer.id)
    );

    yield put(
      changeCollectionStateSuccess(
        set(cloneDeep(collection), 'mainCustomers', updatedMainCustomers)
      )
    );
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const setEndCustomerEmailSubscriptionSaga = function* ({
  payload
}: ISagaAction<IEndCustomerProps>): SagaIterator {
  try {
    const collection = yield select(getCollectionsNew);
    const response = yield call(Api.EndCustomer.setEndCustomerSubscription, {
      endCustomerId: payload.endCustomerId,
      value: payload.value
    });
    ApiErrors.checkOnApiError(response);
    const { value } = response.result;

    const updatedCollection = cloneDeep(collection);
    const currentEndCustomer = updatedCollection.mainCustomers.find(
      (customer: IEndCustomer) => customer.id === payload.endCustomerId
    );
    currentEndCustomer.marketingEmailsEnabled = value;

    yield put(changeCollectionStateSuccess(updatedCollection));
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const changeCollectionStateSaga = function* ({
  payload
}: ISagaAction<ICollectionUpdateAction>): SagaIterator {
  const collection = yield select(getCollectionsNew);
  const updatedCollection = set(cloneDeep(collection), payload.path, payload.value);
  yield put(changeCollectionStateSuccess(updatedCollection));
};

export const getPinSaga = function* (): SagaIterator {
  try {
    let collectionID = yield select(getCollectionId);

    if (!Boolean(collectionID.length)) {
      yield take(fetchCollectionSuccess);
    }
    collectionID = yield select(getCollectionId);
    const response = yield call(Api.Collections.getPin, {
      collection_id: collectionID
    });

    ApiErrors.checkOnApiError(response);

    yield put(changeCollectionStateSuccess(response));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const setAppIconSaga = function* ({
  payload
}: ISagaAction<IUploadAppIconAction>): SagaIterator {
  const { imageID, blob } = payload;
  const collectionID = yield select(getCollectionId);

  try {
    const { originalImageName, gallery_id } = yield select((state) => getImageByID(state)(imageID));

    const signedUrlResponse = yield call(Api.Collections.uploadAppIcon, {
      collection_id: collectionID,
      originalImageName,
      albumId: gallery_id
    });

    ApiErrors.checkOnApiError(signedUrlResponse);
    const { signedUrl, newImage } = signedUrlResponse;

    yield call(multipleAttempts(uploadImageToS3), signedUrl, blob, {});
    yield call(checkIsAppImageOnS3Saga, newImage, 0, signedUrl, blob);
    yield put(fetchImagesSuccess({ appIcon: newImage }));
    yield put(setIsCropped(true));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(setIsCropped(true));
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

const checkIsAppImageOnS3Saga = function* (
  newImage: IImage,
  attempt: number,
  signedUrl: string,
  blob: Blob
): SagaIterator {
  const response = yield call(multipleAttempts(Api.Images.checkS3Exist), {
    image_id: newImage._id,
    size: 'L'
  });

  if (response.error) {
    if (attempt < 4) {
      console.warn('==========================');
      console.warn(`Attempt ${attempt}`);
      console.warn('==========================');
      yield call(multipleAttempts(uploadImageToS3), signedUrl, blob, {});
      yield call(multipleAttempts(uploadImageToS3), signedUrl, blob, {});

      return yield call(checkIsAppImageOnS3Saga, newImage, attempt + 1, signedUrl, blob);
    }
    const errorText = getTranslationKey('errors.s3Request')
      // tslint:disable-next-line:no-invalid-template-strings
        .replace('${ image.originalImageName }', newImage.originalImageName); // eslint-disable-line
    throw Error(errorText);
  } else {
    return true;
  }
};

export const setCollectionHeaderSaga = function* ({
  payload
}: ISagaAction<IUploadCollectionHeaderAction>): SagaIterator {
  const { file } = payload;
  const collectionID = yield select(getCollectionId);

  try {
    const currentCollectionHeader = yield select(getCollectionHeaderImage);

    const currentId = currentCollectionHeader ? currentCollectionHeader._id : null;

    const signedUrlResponse = yield call(multipleAttempts(Api.Images.preUploadHeaderImage), {
      originalImageName: file.name,
      collectionId: collectionID
    });

    ApiErrors.checkOnApiError(signedUrlResponse);
    const { signedUrl, image } = signedUrlResponse.result;

    const imageCompressor = new ImageCompressor(file);
    // @ts-ignore
    const imageFile = yield imageCompressor.compress(file, {
      quality: 0.9,
      maxWidth: 2000
    });

    yield call(multipleAttempts(() => axiosWithoutCredentials.put(signedUrl, imageFile)));

    const images = yield select(getImagesList);
    const updated = [...images.filter((image: IImage) => image._id !== currentId), image];

    yield put(fetchImagesSuccess({ items: updated }));

    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

// Hard timeout for direct-to-S3 PUTs. Without this, requests can hang
// forever when the underlying TCP connection dies silently (e.g. after a
// laptop sleep cycle) — axios has no built-in idle detection, so the
// promise never resolves and the saga's `take(imageUploadingNext)` deadlocks.
// 5 minutes is generous enough for very large files on slow connections
// while still bounding sleep-zombie waits.
const S3_PUT_TIMEOUT_MS = 10 * 60 * 1000;

export const uploadImageToS3 = function* (signedUrl: string, blob: Blob, options: any = {}) {
  // @ts-ignore
  return yield axiosWithoutCredentials.put(signedUrl, blob, {
    timeout: S3_PUT_TIMEOUT_MS,
    ...options
  });
};
